import { computed, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { catchError, map, of, startWith, switchMap } from "rxjs";
import { SelectOption, SelectState } from "@corp-ais/omniark-ui";
import { LocaleString } from "@app/shared/models/agreement.model";
import {
  AgreementContentStore,
  AgreementPage,
} from "./agreement-content.store";

/** Value of the disabled row that reports loading, failure and truncation. */
export const AGREEMENT_STATUS_ROW = "__agreement_status__";

type SearchResult =
  | { readonly state: "idle" }
  | { readonly state: "loading" }
  | { readonly state: "failed" }
  | { readonly state: "ready"; readonly page: AgreementPage };

/**
 * Server-side search for an Agreement picker (`enr-select`).
 *
 * Nothing is fetched until the picker is hovered or opened; then one page
 * per typed term. `enr-select` still filters its options by label on top,
 * so every label it receives must contain the term: {@link label} and
 * {@link statusRow} take care of that.
 *
 * Construct in an injection context (it uses `toSignal`).
 */
export class AgreementSearch {
  private readonly started = signal(false);
  private readonly retries = signal(0);
  /** The term as typed in the picker's search box. */
  readonly term = signal("");

  private readonly result = toSignal(
    toObservable(
      computed(() =>
        this.started() ? { term: this.term(), retry: this.retries() } : null,
      ),
    ).pipe(
      switchMap((query) =>
        query
          ? this.store.search(query.term).pipe(
              map((page): SearchResult => ({ state: "ready", page })),
              startWith<SearchResult>({ state: "loading" }),
              catchError(() => of<SearchResult>({ state: "failed" })),
            )
          : of<SearchResult>({ state: "idle" }),
      ),
    ),
    { initialValue: { state: "idle" } as SearchResult },
  );

  /** The agreements matching the current term. */
  readonly hits = computed(() => {
    const r = this.result();
    return r.state === "ready" ? r.page.items : [];
  });

  /** A disabled row saying what the list holds, or `null` when it is whole. */
  readonly statusRow = computed<SelectOption | null>(() => {
    const r = this.result();
    const term = this.term().trim();
    const quoted = `"${term}"`;
    let label: string | null = null;
    if (r.state === "loading")
      label = term ? `Searching for ${quoted}…` : "Loading Agreements…";
    else if (r.state === "failed")
      label = term
        ? `Couldn't search for ${quoted} · reopen to retry`
        : "Couldn't load Agreements · reopen to retry";
    else if (r.state === "ready") {
      const { items, total } = r.page;
      if (total > items.length)
        label = term
          ? `Showing ${items.length} of ${total} matches for ${quoted} · keep typing to narrow`
          : `Showing ${items.length} of ${total} · type to search`;
      else if (!items.length && term) label = `No Agreement matches ${quoted}`;
    }
    return label
      ? { value: AGREEMENT_STATUS_ROW, label, disabled: true }
      : null;
  });

  constructor(private readonly store: AgreementContentStore) {}

  /** Wire to `(stateChange)`, passing the select itself. */
  onState(state: SelectState, select?: { onSearchClear(): void }): void {
    if (state !== "hover" && state !== "open") return;
    if (!this.started()) this.started.set(true);
    else if (this.result().state === "failed")
      this.retries.update((n) => n + 1);
    // Opening empties the select's search box without telling us, and its
    // de-duplication would then swallow the same term typed again.
    if (state === "open" && this.term()) {
      this.term.set("");
      select?.onSearchClear();
    }
  }

  /** Wire to `(searchChange)`. */
  onSearch(term: string): void {
    this.term.set(term);
  }

  /** `shown`, plus the name that matched when it is in another locale, so
   *  the select's own label filter keeps the row. */
  label(name: LocaleString | undefined, shown: string): string {
    const term = this.term().trim().toLowerCase();
    if (!term || shown.toLowerCase().includes(term)) return shown;
    const match = Object.values(name ?? {}).find((v) =>
      v?.toLowerCase().includes(term),
    );
    return match ? `${shown} · ${match}` : shown;
  }
}
